// apps/client/src/game/interactions/dig.ts (M2-T09) — with the shovel in hand outdoors, A digs your nearest crack within reach:
// the dig anim, SFX and hidden crack are optimistic (undone on reject), then you hold up the fossil and a toast says so.
import { tuning } from '@whiskerton/content';
import type { DigSpotView } from '@whiskerton/shared';
import { digLayerOf } from '../../world/digging';
import type { Candidate, InteractCtx, Interaction } from '../interact';

const SHOVEL = 'tool.shovel';
const centre = (s: DigSpotView) => ({ x: s.x + 0.5, y: s.y + 0.5 });

function nearest(ctx: InteractCtx): Candidate | null {
  if (ctx.space !== 'out' || ctx.tool !== SHOVEL) return null;
  let best: { s: DigSpotView; d: number } | null = null;
  for (const s of ctx.store.you?.yields.digSpots ?? []) {
    const c = centre(s), d = Math.hypot(c.x - ctx.pos.x, c.y - ctx.pos.y);
    if (d <= tuning.town.interactRange && (!best || d < best.d)) best = { s, d };
  }
  return best ? { id: best.s.id, at: centre(best.s), verb: 'hud.verbDig', priority: 60 } : null;
}

export const interaction: Interaction = {
  id: 'dig',
  find: nearest,
  run(ctx, c) {
    const layer = digLayerOf(ctx.game);
    const avatar = ctx.game.you()?.avatar;
    void ctx.game.cmd.act('dig.dig', { spot: c.id }, {
      optimistic: () => { avatar?.play('dig'); ctx.game.sound.sfx('dig'); layer?.hide(c.id, true); return () => layer?.hide(c.id, false); },
    }).then((res) => {
      if (!res.ok) return;
      void ctx.game.assets.model(res.r.item.catalogId).then((m) => { avatar?.hold(m, 'both'); avatar?.play('holdUp', () => avatar.hold(null)); });
      ctx.store.toast('fossils.found');
    });
  },
  fromTap(ctx, hit) {
    if (ctx.tool !== SHOVEL || ctx.space !== 'out') return null;
    const s = (ctx.store.you?.yields.digSpots ?? []).find((x) => x.x === hit.tile.x && x.y === hit.tile.y);
    return s ? { id: s.id, at: centre(s), verb: 'hud.verbDig', priority: 60 } : null;
  },
};
